import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { Skeleton } from './ui/Skeleton';
import { ArticleImage } from './ui/ArticleImage';
import { formatEUR, specLabel, specValue } from '../lib/format';

/**
 * The sheet for a part we DON'T carry.
 *
 * Shared by two surfaces, which is why it lives here rather than inside a page: the catalogue opens
 * it as a modal, and `/catalog/:id` renders it as a full page so a supplier article has a real,
 * linkable URL (the orders page's "More information" links there).
 *
 * There is no product record behind it — identity comes from TecDoc and commerce from the live
 * supplier quote, so nothing here is a projection of one of our own rows.
 */
export interface SupplierArticleDetail {
  reference: string;
  designation: string | null;
  brand: string | null;
  imageUrls: string[];
  oemReferences: string[];
  crossReferences: string[];
  eans: string[];
  specifications: { label: string; value: string; unit?: string | null }[];
  vehicleCount: number;
  tecdocArticleId: number | null;
  identityUnavailable: boolean;
  offers: {
    supplierCode?: string;
    supplierName?: string;
    supplierArticleNo: string;
    matchedNumber: string;
    availableQty: number | null;
    leadTimeDays: number | null;
    unitPriceHT?: number;
    indicativeSellPriceHT: number | null;
  }[];
}

/**
 * A synthetic supplier row id — `sup:<SUPPLIER_CODE>:<url-encoded manufacturer reference>`.
 * Returns the reference, or null when the id is an ordinary product UUID.
 */
export function supplierRefFromId(id: string): string | null {
  if (!id.startsWith('sup:')) return null;
  const rest = id.slice(4);
  const sep = rest.indexOf(':');
  if (sep < 0) return null;
  const raw = rest.slice(sep + 1);
  try {
    return decodeURIComponent(raw) || null;
  } catch {
    return raw || null; // not encoded (older link) — use it as-is rather than failing
  }
}

export function useSupplierArticle(reference: string) {
  const [detail, setDetail] = useState<SupplierArticleDetail | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setFailed(false);
    (async () => {
      try {
        const r = await apiClient.get<SupplierArticleDetail>(
          `/catalog/supplier-articles?ref=${encodeURIComponent(reference)}`
        );
        if (alive) setDetail(r.data);
      } catch {
        // Deliberately not surfaced as a toast: on a page load this IS the content, and the body
        // below already says so. A toast on top of an explanatory empty state is just noise.
        if (alive) setFailed(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [reference]);

  return { detail, failed };
}

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="contents">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="font-mono text-xs break-all">{value}</dd>
  </div>
);

export function SupplierArticleBody({
  detail: d,
  failed,
  reference,
}: {
  detail: SupplierArticleDetail | null;
  failed: boolean;
  reference: string;
}) {
  const { t } = useTranslation();
  if (!d) {
    return failed ? (
      <div className="rounded border bg-card p-4 text-sm">
        <p className="font-medium text-warning">
          {t('productAdmin.supplierArticle.notFound', { reference })}
        </p>
        <p className="mt-1 text-muted-foreground">
          {t('productAdmin.supplierArticle.notFoundDetail')}
        </p>
      </div>
    ) : (
      <Skeleton className="h-40 w-full" />
    );
  }

  return (
    <>
      <ArticleImage
        src={d.imageUrls[0]}
        brandSrc={d.brand ? `/catalog/media/brand/${encodeURIComponent(d.brand)}` : null}
        alt={d.designation ?? d.reference}
        size={140}
      />

      <section className="border rounded p-4 bg-card">
        <h3 className="font-semibold mb-2">{t('productAdmin.supplierArticle.identifiers')}</h3>
        {d.identityUnavailable && (
          <p className="mb-2 text-xs text-warning">
            {t('productAdmin.supplierArticle.tecdocUnreachable')}
          </p>
        )}
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <Row label={t('productAdmin.supplierArticle.fields.reference')} value={d.reference} />
          <Row label={t('productAdmin.supplierArticle.fields.brand')} value={d.brand ?? '—'} />
          <Row
            label={t('productAdmin.supplierArticle.fields.designation')}
            value={d.designation ?? '—'}
          />
          <Row
            label={t('productAdmin.supplierArticle.fields.ean')}
            value={d.eans.join(', ') || '—'}
          />
          <Row
            label={t('productAdmin.supplierArticle.fields.oemReferences')}
            value={d.oemReferences.join(', ') || '—'}
          />
          <Row
            label={t('productAdmin.supplierArticle.fields.crossReferences')}
            value={d.crossReferences.join(', ') || '—'}
          />
          <Row
            label={t('productAdmin.supplierArticle.fields.vehicleCompatibility')}
            value={
              d.vehicleCount > 0
                ? t('productAdmin.supplierArticle.vehicleCount', { count: d.vehicleCount })
                : '—'
            }
          />
          <Row
            label={t('productAdmin.supplierArticle.fields.tecdocArticleId')}
            value={d.tecdocArticleId ?? '—'}
          />
        </dl>
      </section>

      {d.specifications.length > 0 && (
        <section className="border rounded p-4 bg-card">
          <h3 className="font-semibold mb-2">{t('productAdmin.supplierArticle.technicalData')}</h3>
          <dl className="grid grid-cols-2 gap-y-1 text-sm">
            {d.specifications.map((s) => (
              <Row key={s.label} label={specLabel(s)} value={specValue(s)} />
            ))}
          </dl>
        </section>
      )}

      <section className="border rounded p-4 bg-card">
        <h3 className="font-semibold mb-2">{t('productAdmin.supplierArticle.availability')}</h3>
        <div className="text-sm">
          {t('productAdmin.supplierArticle.internalStock')} <strong>0</strong>{' '}
          <span className="text-muted-foreground">
            {t('productAdmin.supplierArticle.notCarried')}
          </span>
        </div>
        {d.offers.length === 0 ? (
          <p className="mt-2 text-sm text-warning">{t('productAdmin.supplierArticle.noOffer')}</p>
        ) : (
          <ul className="mt-2 space-y-1 text-xs">
            {d.offers.map((o) => (
              <li key={`${o.supplierCode ?? 'sup'}:${o.supplierArticleNo}`}>
                <span className="font-mono">
                  {o.supplierName ?? t('productAdmin.supplierArticle.supplier')}
                </span>
                <span className="ml-2 text-success">
                  {t('productAdmin.supplierArticle.available', { qty: o.availableQty ?? '—' })}
                </span>
                {o.leadTimeDays != null && (
                  <span className="ml-2">
                    ·{' '}
                    {o.leadTimeDays === 0
                      ? t('productAdmin.supplierArticle.shipsToday')
                      : t('productAdmin.supplierArticle.leadDays', { days: o.leadTimeDays })}
                  </span>
                )}
                {o.indicativeSellPriceHT != null && (
                  <span className="ml-2" title={t('productAdmin.supplierArticle.indicativeTitle')}>
                    ≈ {formatEUR(o.indicativeSellPriceHT)} HT
                  </span>
                )}
                {o.unitPriceHT != null && (
                  <span className="ml-2 text-muted-foreground">
                    {t('productAdmin.supplierArticle.costRef', {
                      cost: formatEUR(o.unitPriceHT),
                      ref: o.supplierArticleNo,
                    })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          {t('productAdmin.supplierArticle.howToSell')}
        </p>
      </section>
    </>
  );
}
